import {
  Box,
  Dialog,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowForwardRounded,
  CalendarMonthRounded,
  CloseRounded,
  CompareArrowsRounded,
  InsightsRounded,
  QueryStatsRounded,
  SvgIconComponent,
  TrackChangesRounded,
  TravelExploreRounded,
} from "@mui/icons-material";
import { useCorpusStats } from "./hooks/useCorpusStats";
import { overlaySurface } from "../Dashboard/overviewPanel";

/** Icon column width + its gap — the rail everything in a section aligns to. */
const RAIL = "26px";

interface Capability {
  icon: SvgIconComponent;
  title: string;
  /** One line, plain language — what the user gets, not which tool runs. */
  blurb: string;
  examples: string[];
}

/**
 * Order is a pitch, not a taxonomy. The cross-company sweep leads because it
 * is the one thing a user cannot get anywhere else and the one they would
 * never think to try; the single-company transcript search follows as its
 * obvious next step. Fundamentals and price targets sit in the middle as the
 * expected table stakes, and charts and the calendar close as utilities.
 */
const CAPABILITIES: Capability[] = [
  {
    icon: TravelExploreRounded,
    // Named for what the user GETS, not for the mechanism. "Search every
    // company at once" describes our implementation; it leaves the reader to
    // work out why that matters. The value is the inversion: normally you need
    // a company before you can research it, and this needs only an idea.
    title: "Start with a theme, not a ticker",
    blurb:
      "Research usually begins with a company you already know. Ask a question instead, and Insight reads every company's earnings calls to find the ones doing what you described.",
    examples: [
      "Which companies are talking about AI capex?",
      "Are there any companies making lasers to counter drones?",
    ],
  },
  {
    icon: InsightsRounded,
    // Deliberately paired with the section above: that one is breadth, this
    // one is depth. Read together they give the user the whole mental model
    // in two lines.
    title: "Go deep on one company",
    blurb:
      "Product ramps, order backlogs, the target they set two quarters ago. If management said it on an earnings call, you can ask about it and read the quote.",
    examples: [
      "How big is Oracle's revenue backlog?",
      "What did Nvidia say about data centre demand?",
    ],
  },
  {
    icon: QueryStatsRounded,
    title: "Dig into the numbers",
    blurb:
      "Anything the app charts, you can ask about instead. Figures are pulled from the filings at the moment you ask, never recalled from training data.",
    examples: [
      "How has Tesla's free cash flow trended?",
      "Break down Costco's valuation multiples",
    ],
  },
  {
    icon: TrackChangesRounded,
    title: "See where analysts think it's going",
    // Worded to match what the agent is actually allowed to say: it presents
    // WALL STREET's consensus, attributed, and never a view of its own.
    // Insight does not rate stocks or predict prices, so promising more here
    // would set up an expectation the answer then has to refuse.
    blurb:
      "Consensus price targets from the analysts covering a stock, charted against its price history.",
    examples: [
      "Show me the price target for Rocket Lab",
      "What are analysts' price targets for Nebius?",
    ],
  },
  {
    icon: CompareArrowsRounded,
    title: "Chart it and compare",
    blurb:
      "Describe the chart you want and it picks the right one. Name two companies and they land on the same axes.",
    examples: [
      "Show me Apple's revenue chart over the last five years",
      "Compare Microsoft and Alphabet's margins",
    ],
  },
  {
    icon: CalendarMonthRounded,
    title: "Stay ahead of the calendar",
    blurb:
      "Upcoming earnings dates, plus the macro releases that tend to move markets.",
    examples: [
      "What's on the economic calendar this week?",
      "When does Amazon next report earnings?",
    ],
  },
];

interface ChatCapabilitiesSheetProps {
  open: boolean;
  onClose: () => void;
  /** Starts a chat with the tapped example — same path as the composer. */
  onAsk: (prompt: string) => void;
}

/**
 * "How can I use Insight AI?" — answered by the app, not by the agent.
 *
 * The obvious build is to send that question to the chat like any other
 * preset. Three things make that the wrong call, and they only surface once
 * you read the agent's own configuration:
 *
 *  1. The input guard classifies meta questions, and "how do I use you" sits
 *     right on the line between off_topic ("general chitchat") and
 *     prompt_attack ("extract the assistant's role"). A refusal here lands on
 *     the first tap of the first screen.
 *  2. The system prompt FORBIDS the agent from naming its tools or telling the
 *     user to invoke anything — so even a good answer has to talk around the
 *     exact capabilities we want named.
 *  3. It would improvise the pitch, differently every time, and bill credits
 *     for a question whose answer we already know.
 *
 * So this is static UI: instant, free, identical for everyone, and worded by
 * us. The examples are live — tapping one starts a real chat — which turns the
 * pitch into the product rather than a page about it.
 *
 * LAYOUT — the first pass put five sections of near-equal-weight rows on one
 * flat plane and it read as noise (Ben, 2026-07-31: "a lot thrown onto a
 * page"). The fix is depth, not decoration. Each section is now four ranks
 * that step down in size, weight and colour — icon+title, blurb, "TRY ASKING",
 * examples — and everything under the title indents to a single rail. That
 * rail is what makes a section scan as ONE object with a heading rather than
 * four unrelated things stacked in a column.
 */
export const ChatCapabilitiesSheet = ({
  open,
  onClose,
  onAsk,
}: ChatCapabilitiesSheetProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const stats = useCorpusStats();

  const hairline = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)";
  const paper = overlaySurface(isDark);

  const handleAsk = (prompt: string) => {
    onClose();
    onAsk(prompt);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      slotProps={{
        // Lighter than the standard scrim: this sheet explains the app, so
        // leaving the shell visible behind it keeps the user located. At 0.5
        // the app disappeared and this read as a separate page.
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.35)" } },
      }}
      PaperProps={{
        sx: {
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: fullScreen ? "100%" : "min(720px, 94vw)",
          maxHeight: fullScreen ? "100%" : "88vh",
          m: 0,
          borderRadius: fullScreen ? 0 : 3,
          backgroundColor: paper,
          backgroundImage: "none",
          border: fullScreen ? "none" : `1px solid ${hairline}`,
        },
      }}
    >
      {/* Floats over the scroller instead of anchoring a fixed header bar. The
          title is read once and is then just a heading — pinning it spends a
          permanent strip of a long sheet saying nothing new. Only the escape
          hatch has to stay reachable, so only it stays put. Fill plus blur
          keep it legible as content passes underneath. */}
      <IconButton
        onClick={onClose}
        aria-label="Close"
        size="small"
        sx={{
          position: "absolute",
          top: fullScreen ? "calc(12px + env(safe-area-inset-top))" : 12,
          right: 12,
          zIndex: 2,
          color: theme.palette.text.secondary,
          backgroundColor: isDark
            ? "rgba(33, 33, 33, 0.88)"
            : "rgba(255, 255, 255, 0.88)",
          "&:hover": {
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.06)",
          },
        }}
      >
        <CloseRounded sx={{ fontSize: 19 }} />
      </IconButton>

      <Box
        sx={{
          overflowY: "auto",
          px: { xs: 2.5, sm: 4 },
          pt: { xs: 3, sm: 3.5 },
          pb: `calc(${theme.spacing(4)} + env(safe-area-inset-bottom))`,
          mt: fullScreen ? "env(safe-area-inset-top)" : 0,
        }}
      >
        {/* Scrolls away with everything else. `pr` holds the title clear of
            the floating close button. */}
        <Box sx={{ pr: 5, mb: { xs: 3, sm: 4 } }}>
          <Box
            component="h2"
            sx={{
              m: 0,
              fontSize: "1.15rem",
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: theme.palette.text.primary,
            }}
          >
            What Insight AI can do
          </Box>
          {/* The corpus number IS the pitch: specific, verifiable, and nobody
              expects it. Stated once up front rather than per section. */}
          <Box
            sx={{
              mt: 0.75,
              fontSize: "0.85rem",
              lineHeight: 1.55,
              color: theme.palette.text.secondary,
            }}
          >
            Ask in plain English. Every answer is built from{" "}
            <Box component="span" sx={{ color: "#22c55e", fontWeight: 600 }}>
              {stats.calls.toLocaleString()}
            </Box>{" "}
            earnings calls and live company data, never from memory.
          </Box>
        </Box>

        {CAPABILITIES.map(({ icon: Icon, title, blurb, examples }, index) => {
          const isLast = index === CAPABILITIES.length - 1;
          return (
            <Box
              key={title}
              sx={{
                pt: index === 0 ? 0 : { xs: 3, sm: 3.5 },
                pb: isLast ? 0 : { xs: 3, sm: 3.5 },
                borderBottom: isLast ? "none" : `1px solid ${hairline}`,
              }}
            >
              {/* Rank 1 — the theme. Only the icon breaks the rail, so it
                  reads as a marker FOR the section rather than part of it. */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Icon sx={{ fontSize: 16, color: "#22c55e", flex: "none" }} />
                <Box
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    letterSpacing: "-0.005em",
                    color: theme.palette.text.primary,
                  }}
                >
                  {title}
                </Box>
              </Box>

              <Box sx={{ pl: RAIL }}>
                {/* Rank 2 — what it means. */}
                <Box
                  sx={{
                    mt: 0.5,
                    fontSize: "0.82rem",
                    lineHeight: 1.55,
                    color: theme.palette.text.secondary,
                  }}
                >
                  {blurb}
                </Box>

                {/* Rank 3 — the label that turns the examples from a list of
                    statements into an invitation. */}
                <Box
                  sx={{
                    mt: 2,
                    mb: 1,
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.09em",
                    color: theme.palette.text.disabled,
                  }}
                >
                  Try asking
                </Box>

                {/* Rank 4 — two up. The extra sheet width is what buys this:
                    side by side they read as sibling examples of one idea,
                    where stacked full-width rows read as a queue of separate
                    instructions. */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1,
                  }}
                >
                  {examples.map((example) => (
                    <Box
                      key={example}
                      component="button"
                      type="button"
                      onClick={() => handleAsk(example)}
                      sx={{
                        display: "flex",
                        // Top, not centre: the grid stretches both cards to the
                        // taller one, and centring floats a one-line example
                        // below its neighbour's first line.
                        alignItems: "flex-start",
                        gap: 1,
                        width: "100%",
                        height: "100%",
                        textAlign: "left",
                        px: 1.5,
                        py: 1.15,
                        cursor: "pointer",
                        borderRadius: 1.5,
                        border: `1px solid ${hairline}`,
                        backgroundColor: "transparent",
                        fontSize: "0.78rem",
                        fontWeight: 500,
                        lineHeight: 1.4,
                        fontFamily: "inherit",
                        color: theme.palette.text.secondary,
                        transition:
                          "background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease",
                        "&:hover": {
                          backgroundColor: isDark
                            ? "rgba(255, 255, 255, 0.04)"
                            : "rgba(0, 0, 0, 0.03)",
                          borderColor: "rgba(34, 197, 94, 0.4)",
                          color: theme.palette.text.primary,
                        },
                        "&:hover .exampleArrow": {
                          opacity: 1,
                          transform: "translateX(2px)",
                        },
                        "&:focus-visible": {
                          outline: "2px solid #22c55e",
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                        {example}
                      </Box>
                      <ArrowForwardRounded
                        className="exampleArrow"
                        sx={{
                          fontSize: 13,
                          flex: "none",
                          mt: "2px",
                          opacity: 0.4,
                          transition:
                            "opacity 0.18s ease, transform 0.18s ease",
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Dialog>
  );
};
